import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GetStaffFilters, staffService } from "@/services/staffService";
import {
  CreateFrontDeskCustomRoleUserDto,
  CreateStaffUserDto,
  UpdateUserDto,
} from "@/types/user";
import { useToast } from "@/components/ui/use-toast";

export const useStaff = (
  filters?: GetStaffFilters,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ["staff", filters],
    queryFn: () => staffService.getStaff(filters),
    enabled: options?.enabled ?? true,
  });
};

export const useStaffPositions = (hotelId: string) => {
  return useQuery({
    queryKey: ["staff-positions", hotelId],
    queryFn: async () => {
      if (!hotelId) return;
      const data = await staffService.getStaffPositions(hotelId);
      return data.positions;
    },
  });
};

export const useStaffSearch = (
  filters?: GetStaffFilters,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ["staff-search", filters],
    queryFn: () => staffService.searchStaff(filters),
    enabled: options?.enabled,
  });
};

export const useStaffById = (id: string) => {
  return useQuery({
    queryKey: ["staff", id],
    queryFn: () => staffService.getStaffById(id),
    enabled: !!id,
  });
};

export const useCreateStaff = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateStaffUserDto) => staffService.createStaff(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      queryClient.invalidateQueries({ queryKey: ["staff-postions"] });
      toast({
        title: "Success",
        description: "Staff member created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create staff member",
        variant: "destructive",
      });
    },
  });
};

export const useCreateFrontDesk = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateFrontDeskCustomRoleUserDto) =>
      staffService.createFrontDesk(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
      queryClient.invalidateQueries({ queryKey: ["custom-roles"] });
      toast({
        title: "Success",
        description: "Frontdesk user created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create Frontdesk user",
        variant: "destructive",
      });
    },
  });
};

export const useToggleFrontDeskActive = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      staffService.updateFrontDeskStatus(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
      toast({
        title: "Success",
        description: "Frontdesk status updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update Frontdesk status",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateStaff = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserDto }) =>
      staffService.updateStaff(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      toast({
        title: "Success",
        description: "Staff member updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update staff member",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteStaff = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => staffService.deleteStaff(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      toast({
        title: "Success",
        description: "Staff member deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete staff member",
        variant: "destructive",
      });
    },
  });
};

export const useSendPasswordReset = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => staffService.sendPasswordResetEmail(id),
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Password reset email sent successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send password reset email",
        variant: "destructive",
      });
    },
  });
};
